import React from "react";
import { useTranslation } from "react-i18next";
import BaseChip from "@reusables/BaseChip";
import { cn } from "@helpers/utils";
import { StockCount } from "@/types/general";

const CHIP_FILL: Record<StockCount.Status, string> = {
    0: "yellow",
    1: "blue",
    2: "red",
    3: "green",
};

/**
 * Saturated counterparts of the chip fills. BaseChip's own `asDot` palette reuses the chip
 * *background* colours, which are built to sit behind dark text — at 7px they measure between
 * 1.17 and 1.82 contrast on white, i.e. they read as a missing dot rather than a subtle one.
 * Classes are spelled out in full so Tailwind's scanner can see them.
 */
const DOT_FILL: Record<StockCount.Status, string> = {
    0: "bg-[#FFC700]", // alerts.warning
    1: "bg-[#70B6F6]", // badge.blue, without its 24% alpha
    2: "bg-error",
    3: "bg-positive",
};

/**
 * Status of a stock count. Shared between the main table column and the merge origins tree,
 * so both surfaces stay in sync when statuses change.
 *
 * `asDot` swaps the filled chip for a coloured dot plus plain text. The origins tree uses it to
 * keep status legible without letting it out-shout the row it belongs to.
 */
export default function StockCountStatusChip({
    status,
    asDot = false,
}: {
    status: StockCount.Status;
    asDot?: boolean;
}) {
    const { t } = useTranslation("", { keyPrefix: "inventory.stockCount.main" });

    const label = t(`status.${status}`);

    if (asDot) {
        return (
            // Deliberately NOT a flex container. An inline-flex box aligns by its own box edges,
            // which lifts the whole label off the surrounding baseline. Leaving the label as plain
            // inline text means it inherits the baseline for free, and only the dot — an empty
            // inline-block — needs vertical centring.
            <span className="whitespace-nowrap text-accent">
                <span
                    className={cn(
                        "mr-[6px] inline-block h-[7px] w-[7px] rounded-full align-middle",
                        DOT_FILL[status]
                    )}
                />
                {label}
            </span>
        );
    }

    return <BaseChip fill={CHIP_FILL[status]}>{label}</BaseChip>;
}
